import { useEffect, useRef, useState } from 'react'

import {
  EVENT_NAME_MOUSE_MOVE,
  EVENT_NAME_MOUSE_UP,
} from '../../../../constants/event'
import {
  isMainMouseButton,
  preventDefault,
  stopPropagation,
} from '../../../../utils/events'

import {
  checkIsSelectBoxDisabled,
  getDraggedSelectBoxCoordinates,
  getResizedSelectBoxSize,
  getSelectBoxCoordinates,
} from './usePdfViewerSelectionAreaBox.utils'

export function usePdfViewerSelectionAreaBoxGestures({
  commitSelectBoxRect,
  maxResizeHeightRef,
  maxResizeWidthRef,
  minHeight,
  minWidth,
  pageRectRef,
  pdfViewerInstance,
  selectBoxRef,
  updateSelectBoxRect,
}) {
  const [isChanged, setChanged] = useState(null)
  const [isChanging, setChanging] = useState(false)

  const gestureAbortControllerRef = useRef(null)

  // a gesture in flight must not outlive the viewer it started on
  useEffect(() => {
    return () => {
      gestureAbortControllerRef.current?.abort()
    }
  }, [pdfViewerInstance])

  function checkIsGestureAllowed(event) {
    return (
      isMainMouseButton(event) &&
      selectBoxRef.current &&
      pageRectRef.current &&
      !checkIsSelectBoxDisabled(selectBoxRef.current)
    )
  }

  function startGesture() {
    gestureAbortControllerRef.current?.abort()

    const abortController = new AbortController()

    gestureAbortControllerRef.current = abortController

    setChanged(false)
    setChanging(true)

    return abortController.signal
  }

  function endGesture() {
    commitSelectBoxRect()
    setChanged(true)
    setChanging(false)

    gestureAbortControllerRef.current?.abort()
  }

  function listenGesture({ signal, onMouseMove }) {
    document.addEventListener(EVENT_NAME_MOUSE_MOVE, onMouseMove, { signal })
    document.addEventListener(EVENT_NAME_MOUSE_UP, endGesture, { signal })
  }

  function handleDragStart(event) {
    if (checkIsGestureAllowed(event)) {
      const boxSize = selectBoxRef.current.getBoundingClientRect()
      const { left, top } = getSelectBoxCoordinates(selectBoxRef)

      const startCoordinates = {
        x: left ?? boxSize.left,
        y: top ?? boxSize.top,
      }
      const startMouseCoordinates = { x: event.clientX, y: event.clientY }

      function handleMouseMove(moveEvent) {
        const currentBoxSize = selectBoxRef.current.getBoundingClientRect()

        updateSelectBoxRect({
          ...getDraggedSelectBoxCoordinates({
            boxSize: currentBoxSize,
            delta: {
              x: moveEvent.clientX - startMouseCoordinates.x,
              y: moveEvent.clientY - startMouseCoordinates.y,
            },
            pageRect: pageRectRef.current,
            startCoordinates,
          }),
          height: currentBoxSize.height,
          width: currentBoxSize.width,
        })
      }

      listenGesture({ signal: startGesture(), onMouseMove: handleMouseMove })
    }
  }

  function handleResizeMouseDown(event) {
    if (checkIsGestureAllowed(event)) {
      stopPropagation(event)
      preventDefault(event)

      const boxSize = selectBoxRef.current.getBoundingClientRect()

      const startSize = { height: boxSize.height, width: boxSize.width }
      const startMouseCoordinates = { x: event.clientX, y: event.clientY }

      function handleMouseMove(moveEvent) {
        preventDefault(moveEvent)

        const boxCoordinates = getSelectBoxCoordinates(selectBoxRef)

        updateSelectBoxRect({
          ...getResizedSelectBoxSize({
            boxCoordinates,
            delta: {
              x: moveEvent.clientX - startMouseCoordinates.x,
              y: moveEvent.clientY - startMouseCoordinates.y,
            },
            maxHeight: maxResizeHeightRef.current,
            maxWidth: maxResizeWidthRef.current,
            minHeight,
            minWidth,
            pageRect: pageRectRef.current,
            startSize,
          }),
          x: boxCoordinates.left,
          y: boxCoordinates.top,
        })
      }

      listenGesture({ signal: startGesture(), onMouseMove: handleMouseMove })
    }
  }

  return {
    gestureProps: {
      onMouseDown: handleDragStart,
      onResizeMouseDown: handleResizeMouseDown,
    },
    isChanged,
    isChanging,
    setChanged,
  }
}
